import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../cx';
import { IconButton } from '../IconButton/IconButton';
import styles from './Toast.module.css';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastOptions {
  title?: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** Milliseconds before auto-dismiss. 0 means it stays until dismissed. Default 5000. */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: string;
}

interface ToastContextValue {
  /** Returns the toast's id, so a caller can dismiss it early (e.g. once a mutation settles). */
  showToast: (options: ToastOptions) => string;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Must be called under a `ToastProvider` (mounted once, near the app root). */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

/** Like `useToast`, but `null` outside a provider (components that also render in isolated tests). */
export function useOptionalToast(): ToastContextValue | null {
  return useContext(ToastContext);
}

const DEFAULT_DURATION = 5000;

/**
 * Renders transient, self-dismissing notifications in a fixed corner
 * viewport, portaled to `document.body`. Each toast is `role="status"` with
 * `aria-live="polite"` so assistive tech announces it without stealing focus
 * — for a message that truly requires a decision, use ConfirmDialog instead.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = timers.current[id];
    if (timer) {
      clearTimeout(timer);
      delete timers.current[id];
    }
  }, []);

  const showToast = useCallback(
    (options: ToastOptions) => {
      const id = `toast-${Math.random().toString(36).slice(2)}`;
      const duration = options.duration ?? DEFAULT_DURATION;
      setToasts((current) => [...current, { id, ...options }]);
      if (duration > 0) {
        timers.current[id] = setTimeout(() => dismissToast(id), duration);
      }
      return id;
    },
    [dismissToast],
  );

  useEffect(() => {
    const timersAtMount = timers.current;
    return () => {
      Object.values(timersAtMount).forEach(clearTimeout);
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className={styles.viewport} role="region" aria-label="Notifications">
            {toasts.map((t) => (
              <div key={t.id} role="status" aria-live="polite" className={cx(styles.toast, styles[t.tone ?? 'info'])}>
                <div className={styles.content}>
                  {t.title && <div className={styles.title}>{t.title}</div>}
                  {t.description && <div className={styles.description}>{t.description}</div>}
                </div>
                <IconButton
                  aria-label="Dismiss notification"
                  size="sm"
                  onClick={() => dismissToast(t.id)}
                  icon={
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M4 4l8 8m0-8l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  }
                />
              </div>
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}
